import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Flame, Sun, Moon } from "lucide-react";

export default function Journal() {
  const { refresh } = useAuth();
  const [tab, setTab] = useState(new Date().getHours() < 14 ? "am" : "pm");
  const [text, setText] = useState("");
  const [entries, setEntries] = useState([]);
  const [dates, setDates] = useState([]);
  const [streak, setStreak] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/journal");
    setEntries(data.entries); setDates(data.dates); setStreak(data.streak);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/journal", { type: tab, text: text.trim() });
      setStreak(data.streak); setText("");
      await Promise.all([load(), refresh()]);
      toast.success(`Logged! ${data.streak}-day streak 🔥`);
    } finally { setBusy(false); }
  };

  // last 5 weeks grid
  const today = new Date();
  const days = Array.from({ length: 35 }).map((_, i) => {
    const d = new Date(today); d.setDate(today.getDate() - (34 - i));
    return d.toISOString().slice(0, 10);
  });

  const prompt = tab === "am" ? "What are you grateful for this morning?" : "What's one moment you're proud of today?";

  return (
    <div className="relative flex-1 overflow-y-auto mm-scroll bg-white" data-testid="journal-page">
      <div className="relative z-10 px-5 pt-7 pb-8">
        <div className="flex items-center justify-between">
          <h1 className="font-serif text-3xl font-bold text-navy">Journal</h1>
          <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 text-orange-600 rounded-full px-3 py-1.5" data-testid="journal-streak">
            <Flame size={16} className="fill-orange-500 text-orange-500" />
            <span className="font-bold text-sm">{streak} day{streak === 1 ? "" : "s"}</span>
          </div>
        </div>
        <p className="text-slate-400 text-xs mt-1">One grace day keeps your streak alive.</p>

        <div className="mt-5 flex gap-2 bg-slate-100 p-1 rounded-2xl">
          <button data-testid="journal-tab-am" onClick={() => setTab("am")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition ${tab === "am" ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}>
            <Sun size={16} /> Morning
          </button>
          <button data-testid="journal-tab-pm" onClick={() => setTab("pm")}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition ${tab === "pm" ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}>
            <Moon size={16} /> Evening
          </button>
        </div>

        <div className="mt-4">
          <p className="font-serif text-lg text-navy mb-2">{prompt}</p>
          <textarea data-testid="journal-text" value={text} onChange={(e) => setText(e.target.value)} rows={4}
            placeholder="Write freely…"
            className="w-full px-4 py-3 rounded-2xl bg-slate-100 border border-transparent focus:border-royal focus:bg-white outline-none resize-none" />
          <button data-testid="journal-save" disabled={!text.trim() || busy} onClick={save}
            className="mt-3 w-full bg-royal text-white font-bold py-3.5 rounded-2xl disabled:opacity-40 active:scale-[0.98] transition">
            {busy ? "Saving…" : "Save entry"}
          </button>
        </div>

        <div className="mt-7">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2.5">Last 5 weeks</div>
          <div className="grid grid-cols-7 gap-1.5" data-testid="journal-calendar">
            {days.map((d) => (
              <div key={d} className={`aspect-square rounded-lg ${dates.includes(d) ? "bg-royal" : "bg-slate-100"} ${d === today.toISOString().slice(0, 10) ? "ring-2 ring-turq" : ""}`} />
            ))}
          </div>
        </div>

        <div className="mt-7">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2.5">History</div>
          <div className="space-y-2.5">
            {entries.length === 0 && <p className="text-slate-400 text-sm">No entries yet.</p>}
            {entries.map((e) => (
              <div key={e.id} className="rounded-2xl border border-slate-200 p-3.5" data-testid={`journal-entry-${e.id}`}>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                  {e.type === "am" ? <Sun size={13} /> : <Moon size={13} />}
                  {e.type === "am" ? "Gratitude" : "Proud moment"} · {e.date}
                </div>
                <p className="text-navy mt-1.5 text-[15px]">{e.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
